import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { PdfViewerModule } from 'ng2-pdf-viewer';

import { ApiService } from '@core/providers/http';
import {
  TipoDocumentoService,
  BolseiroService,
  BolsaService,
} from '../../api/services';

import { OficioInstituicaoEnsinoComponent } from '../components/oficio-instituicao-ensino/oficio-instituicao-ensino.component';
import { MemorandoPagamentoDeSubsidiosExternosComponent } from '../components/memorando-pagamento-de-subsidios-externos/memorando-pagamento-de-subsidios-externos.component';
import { ComprovativoPagamentoDeSubsidiosComponent } from '../components/comprovativo-pagamento-de-subsidios/comprovativo-pagamento-de-subsidios.component';
import { MemorandoApoioDefesaMonografiaComponent } from '../components/memorando-apoio-defesa-monografia/memorando-apoio-defesa-monografia.component';
import { CancelamentoBolsaComponent } from '../components/cancelamento-bolsa/cancelamento-bolsa.component';
import { DeclaracaoParaEmbaixadaComponent } from '../components/declaracao-para-embaixada/declaracao-para-embaixada.component';
import { FichaDeCadastroComponent } from '../components/ficha-de-cadastro/ficha-de-cadastro.component';
import { PdfPreviewComponent } from '../components/pdf-preview/pdf-preview.component';
import { SafeUrlPipe } from '@app/features-modules/gestao-bolseiros/pipes/safe-url.pipe';

type AllowedDocType = 1 | 3 | 4 | 5 | 6 | 8 | 9;

@Component({
  selector: 'app-emitir-documento-modal',
  templateUrl: './emitir-documento-modal.component.html',
  styleUrls: ['./emitir-documento-modal.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    CommonModule,
    OficioInstituicaoEnsinoComponent,
    MemorandoPagamentoDeSubsidiosExternosComponent,
    ComprovativoPagamentoDeSubsidiosComponent,
    MemorandoApoioDefesaMonografiaComponent,
    CancelamentoBolsaComponent,
    DeclaracaoParaEmbaixadaComponent,
    FichaDeCadastroComponent,
    PdfViewerModule,
    PdfPreviewComponent,
    DialogModule,
    SafeUrlPipe,
  ],
  providers: [
    ApiService,
    TipoDocumentoService,
    BolseiroService,
    BolsaService,
    DialogService,
  ],
})
export class EmitirDocumentoModalComponent implements OnInit, OnDestroy {
  fb = inject(FormBuilder);
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  dialogService = inject(DialogService);
  tipoDocumentoService = inject(TipoDocumentoService);
  bolseiroService = inject(BolseiroService);
  bolsaService = inject(BolsaService);

  form!: FormGroup;

  TypeDocs: any[] = [];
  selectedType: AllowedDocType | '' = '';
  pdfBlob?: string;
  private previewRef?: DynamicDialogRef;

  // estado do filho atual
  childData: any = null;
  childValid = false;
  childEnabled = true;

  // botões reativos
  get canPreview() {
    return !!this.selectedType && this.childValid;
  }
  get canGenerate() {
    return !!this.selectedType && this.childValid;
  }

  constructor() {
    this.form = this.fb.group({
      tipoDocumento: [null, Validators.required],
    });

    // filtra tipos vindos pelo DynamicDialogConfig
    this.TypeDocs = (this.config?.data?.TypeDocs ?? []).filter((t: any) =>
      [1, 4, 5, 6, 8, 9].includes(t.id)
    );
  }

  ngOnInit() {}

  onTypeChange(evt: { value: AllowedDocType }) {
    this.selectedType = (evt?.value ?? '') as AllowedDocType | '';
    // reset estado do filho ao trocar
    this.childData = null;
    this.childValid = false;
  }

  // padrão único de evento vindo dos filhos
  handleChildEvent(evt: { data: any; valid: boolean }) {
    this.childData = evt?.data ?? null;
    this.childValid = !!evt?.valid;
  }

  previewPDF() {
    if (!this.canPreview) {
      this.form.get('tipoDocumento')?.markAsTouched();
      return;
    }

    const selectedType = (this.childData?.tipoDocumento ||
      this.selectedType) as AllowedDocType;

    const map = {
      1: {
        service: this.bolseiroService.exportEnquadramentoAnoLectivo.bind(
          this.bolseiroService
        ),
        data: { ...this.childData, isSave: false },
      },
      3: {
        service: this.bolseiroService.exportMemorando.bind(
          this.bolseiroService
        ),
        data: { ...this.childData, isSave: false },
      },
      4: {
        service: this.bolseiroService.exportDeclaracao.bind(
          this.bolseiroService
        ),
        data: { ...this.childData, isSave: false },
      },
      5: {
        service: this.bolsaService.gerarFichaCadastro.bind(this.bolsaService),
        data: {
          tipoDocumento: 5,
          estadoDocumento: 0,
          utilizadorID: 'string',
          dados: { ...this.childData, isSave: false },
        },
      },
      6: {
        service: this.bolsaService.exportComprovativoSubsidio.bind(
          this.bolsaService
        ),
        data: { ...this.childData, isSave: false },
      },
      8: {
        service: this.bolseiroService.exportCancelamentoBolsa.bind(
          this.bolseiroService
        ),
        data: { ...this.childData, isSave: false },
      },
      9: {
        service: this.bolseiroService.exportMemorandoApoioDefesaMonografia.bind(
          this.bolseiroService
        ),
        data: { ...this.childData, isSave: false },
      },
    } as const;

    const cfg = (map as any)[selectedType];
    if (!cfg) return;

    cfg.service(cfg.data).subscribe((blob: Blob) => {
      if (!(blob instanceof Blob)) return;

      const objectUrl = URL.createObjectURL(blob);
      this.previewRef = this.dialogService.open(PdfPreviewComponent, {
        header: 'Visualizar Documento',
        width: '80%',
        data: { blob },
        closable: true,
      });
      this.previewRef.onClose.subscribe(() => URL.revokeObjectURL(objectUrl));
    });
  }

  onSubmit() {
    const selectedType = (this.childData?.tipoDocumento ||
      this.selectedType) as AllowedDocType;

    const map = {
      1: {
        service: this.bolseiroService.exportEnquadramentoAnoLectivo.bind(
          this.bolseiroService
        ),
        fileName: 'OFÍCIO - INSTITUIÇÃO DE ENSINO.pdf',
      },
      3: {
        service: this.bolseiroService.exportMemorando.bind(
          this.bolseiroService
        ),
        fileName: 'MEMORANDO - PAGAMENTO DE SUBSIDIOS EXTERNO.pdf',
      },
      4: {
        service: this.bolseiroService.exportDeclaracao.bind(
          this.bolseiroService
        ),
        fileName: 'DECLARAÇÃO PARA VISTOS - EMBAIXADAS.pdf',
      },
      5: {
        service: this.bolsaService.gerarFichaCadastro.bind(this.bolsaService),
        fileName: 'FICHA DE CADASTRO.pdf',
      },
      6: {
        service: this.bolsaService.exportComprovativoSubsidio.bind(
          this.bolsaService
        ),
        fileName:
          'COMPROVATIVO DE PAGAMENTO DO SUBSÍDIO DE APOIO À FORMAÇÃO.pdf',
      },
      8: {
        service: this.bolseiroService.exportCancelamentoBolsa.bind(
          this.bolseiroService
        ),
        fileName: 'MEMORANDO - CANCELAMENTO DE BOLSA.pdf',
      },
      9: {
        service: this.bolseiroService.exportMemorandoApoioDefesaMonografia.bind(
          this.bolseiroService
        ),
        fileName: 'MEMORANDO DE APOIO À DEFESA DE MONOGRAFIA.pdf',
      },
    } as const;

    const cfg = (map as any)[selectedType];
    if (!cfg) return;

    const payload =
      selectedType === 5
        ? {
            tipoDocumento: 5,
            estadoDocumento: 0,
            utilizadorID: 'string',
            dados: { ...this.childData, isSave: true },
          }
        : { ...this.childData, isSave: true };

    cfg.service(payload).subscribe((response: Blob) => {
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = cfg.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
  }

  ngOnDestroy() {
    if (this.pdfBlob) URL.revokeObjectURL(this.pdfBlob as any);
  }

  onCancel() {
    this.ref.close();
  }
}
